import { FC, useEffect } from "react";
import { Brick } from "components/Atoms/Brick";
import { useScreenIsAboveBreakpoint } from "hooks/useScreenIsAboveBreakpoint";
import { ComponentMoleculesNavResource } from "types/graphql/generated";
import { useMegaNavMobileContext } from "../hooks/useMegaNavMobileContext/useMegaNavMobileContext";
import { MobileBrickTab } from "../MobileBrickTab";
import { MobileMenuHeaderLabel } from "../MobileMenuHeaderLabel";
import { SubMenuPanel } from "../SubMenuPanel";
import { SubMenuPanelGroup } from "../SubMenuPanelGroup";

interface SubMenuTabGroupProps {
  resource: ComponentMoleculesNavResource;
}

const SubMenuTabGroup: FC<SubMenuTabGroupProps> = ({ resource }) => {
  const isDesktopBreakpoint = useScreenIsAboveBreakpoint("lg");

  const { activeTabIndex, setActiveTabIndex } = useMegaNavMobileContext();
  const firstTabIndexWithoutUrl = resource.subMenuTabs.findIndex((tab) => !tab.tabBrick.url);

  useEffect(() => {
    if (!isDesktopBreakpoint) {
      setActiveTabIndex(-1);
    } else if (firstTabIndexWithoutUrl !== -1) {
      setActiveTabIndex(firstTabIndexWithoutUrl);
    } else {
      setActiveTabIndex(0);
    }
  }, [isDesktopBreakpoint, firstTabIndexWithoutUrl, setActiveTabIndex]);

  return (
    <>
      {isDesktopBreakpoint ? (
        <>
          <SubMenuPanel bgColorType="secondary">
            <div className="flex w-full flex-col gap-2 py-2">
              {resource.subMenuTabs.map((tab, index) => (
                <Brick
                  title={tab.tabBrick.title}
                  subTitle={tab.tabBrick.subTitle}
                  imageUrlHash={tab.tabBrick.image?.data?.attributes?.hash}
                  imageAlt={tab.tabBrick.image?.data?.attributes?.url}
                  imageSize={tab.tabBrick.imageSize}
                  titleTagLabel={tab.tabBrick?.titleTag?.label}
                  titleTagIconUrlHash={tab.tabBrick.titleTag?.icon?.data?.attributes?.hash}
                  titleTagIconAlt={tab.tabBrick.titleTag?.icon?.data?.attributes?.alternativeText}
                  titleTagBgColor={tab.tabBrick.titleTag?.bgColor}
                  href={tab.tabBrick.url}
                  hoverEffect={tab.tabBrick.hoverEffect}
                  key={tab.tabBrick.id}
                  onClick={() => setActiveTabIndex(index)}
                  currentTab={activeTabIndex === index}
                  isTab
                />
              ))}
            </div>
          </SubMenuPanel>
          {resource.subMenuTabs.map(
            (tab, index) => activeTabIndex === index && <SubMenuPanelGroup panels={tab?.linkedPanels} key={tab.id} />
          )}
        </>
      ) : (
        <div className="w-screen">
          <div className="relative flex w-full flex-col">
            {resource.subMenuTabs.map((tab, index) => (
              <MobileBrickTab
                href={tab.tabBrick.url}
                title={tab.tabBrick.title}
                subTitle={tab.tabBrick.subTitle}
                id={tab.tabBrick.id}
                onClick={() => setActiveTabIndex(index)}
                key={tab.tabBrick.id}
              />
            ))}
          </div>
          {resource.subMenuTabs.map(
            (tab, index) =>
              activeTabIndex === index &&
              !tab.tabBrick.url && (
                <div className="absolute top-0 animate-right-to-left-slide-in" key={tab.id}>
                  <MobileMenuHeaderLabel label={tab.tabBrick.title} />
                  <SubMenuPanelGroup panels={tab?.linkedPanels} />
                </div>
              )
          )}
        </div>
      )}
    </>
  );
};

export { SubMenuTabGroup };
